import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Music, Heart } from 'lucide-react';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-burgundy text-white" data-testid="footer">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <Music className="w-6 h-6 text-white" />
              </div>
              <div>
                <span className="font-dancing text-2xl">École de Musique Pour Tous</span>
                <p className="text-sm text-white/70 -mt-1">Périgueux</p>
              </div>
            </div>
            <p className="text-white/80 text-sm leading-relaxed mb-6">
              Cours de musique à Périgueux pour enfants et adultes. Piano, guitare, chant et éveil musical dispensés par Charlotte et son équipe dans une ambiance chaleureuse et bienveillante.
            </p>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="font-nunito font-bold text-lg mb-6">Navigation</h4>
            <ul className="space-y-3">
              {[
                { name: 'Accueil', path: '/' },
                { name: 'Nos cours', path: '/cours' },
                { name: 'Tarifs', path: '/tarifs' },
                { name: 'À propos', path: '/a-propos' },
                { name: 'Avis clients', path: '/avis' },
                { name: 'Contact', path: '/contact' },
              ].map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-white/80 hover:text-white transition-colors text-sm"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Cours */}
          <div>
            <h4 className="font-nunito font-bold text-lg mb-6">Nos Cours à Périgueux</h4>
            <ul className="space-y-3">
              {[
                { name: 'Éveil musical', path: '/cours#eveil', title: 'Cours d\'éveil musical à Périgueux' },
                { name: 'Cours de Piano', path: '/cours#piano', title: 'Cours de piano à Périgueux' },
                { name: 'Cours de Guitare', path: '/cours#guitare', title: 'Cours de guitare à Périgueux' },
                { name: 'Cours de Chant', path: '/cours#chant', title: 'Cours de chant à Périgueux' },
                { name: 'Ateliers sensoriels', path: '/cours#ateliers', title: 'Ateliers sensoriels pour enfants à Périgueux' },
              ].map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    title={link.title}
                    className="text-white/80 hover:text-white transition-colors text-sm"
                    data-testid={`footer-course-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-nunito font-bold text-lg mb-6">Contact</h4>
            <ul className="space-y-4">
              <li>
                <a
                  href="tel:+33649523778"
                  className="flex items-start gap-3 text-white/80 hover:text-white transition-colors group"
                  data-testid="footer-phone"
                >
                  <Phone className="w-5 h-5 mt-0.5 group-hover:scale-110 transition-transform" />
                  <span className="text-sm">06 49 52 37 78</span>
                </a>
              </li>
              <li>
                <a
                  href="mailto:musiquepourtous24@gmail.com"
                  className="flex items-start gap-3 text-white/80 hover:text-white transition-colors group"
                  data-testid="footer-email"
                >
                  <Mail className="w-5 h-5 mt-0.5 group-hover:scale-110 transition-transform" />
                  <span className="text-sm">musiquepourtous24@gmail.com</span>
                </a>
              </li>
              <li>
                <a
                  href="https://maps.google.com/?q=2+Rue+Ludovic+Trarieux,+24000+Périgueux"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 text-white/80 hover:text-white transition-colors group"
                  data-testid="footer-address"
                >
                  <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0 group-hover:scale-110 transition-transform" />
                  <span className="text-sm">
                    2 Rue Ludovic Trarieux<br />
                    24000 Périgueux<br />
                    Quartier Clos Chassaing
                  </span>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/60 text-sm text-center md:text-left">
              © {currentYear} École de Musique Pour Tous - Périgueux. Tous droits réservés.
            </p>
            <div className="flex items-center gap-6">
              <Link
                to="/mentions-legales"
                className="text-white/60 hover:text-white text-sm transition-colors"
                data-testid="footer-legal"
              >
                Mentions légales
              </Link>
              <Link
                to="/confidentialite"
                className="text-white/60 hover:text-white text-sm transition-colors"
                data-testid="footer-privacy"
              >
                Confidentialité
              </Link>
              <Link
                to="/cookies"
                className="text-white/60 hover:text-white text-sm transition-colors"
                data-testid="footer-cookies"
              >
                Cookies
              </Link>
            </div>
          </div>
          <p className="text-white/40 text-xs text-center mt-4 flex items-center justify-center gap-1">
            Fait avec <Heart className="w-3 h-3 text-coral" /> à Périgueux
          </p>
        </div>
      </div>
    </footer>
  );
};
